import { useQuery } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { getPortalLineup, type PortalLineupRow } from "@/api/client";
import type { PortalOutletContext } from "@/pages/artist-portal/ArtistPortalDetailPage";

export function LineupTab() {
  const { festivalId } = useOutletContext<PortalOutletContext>();
  const q = useQuery({
    queryKey: ["artist-portal", festivalId, "lineup"],
    queryFn: () => getPortalLineup(festivalId),
  });

  if (q.isLoading) {
    return <div className="text-ink-3">Loading lineup…</div>;
  }
  if (q.isError) {
    return <div className="text-bad">Could not load lineup.</div>;
  }
  if (!q.data || q.data.length === 0) {
    return <div className="text-ink-3">No lineup yet.</div>;
  }

  const byDay = new Map<string, PortalLineupRow[]>();
  for (const row of q.data) {
    const existing = byDay.get(row.day) ?? [];
    existing.push(row);
    byDay.set(row.day, existing);
  }

  return (
    <div className="space-y-6">
      {[...byDay.entries()].map(([day, rows]) => (
        <section key={day}>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-3">
            {day}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-line-2">
            <table className="w-full min-w-[320px] text-sm">
              <thead className="bg-panel text-ink-3">
                <tr>
                  <th className="p-2 text-left">Time</th>
                  <th className="p-2 text-left">Artist</th>
                  <th className="p-2 text-left">Stage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-2 bg-bg-2">
                {rows.map((r) => (
                  <tr
                    key={`${r.day}-${r.start_time ?? "x"}-${r.artist_name}-${r.stage_name}`}
                  >
                    <td className="p-2 text-ink-2">
                      {r.start_time ?? "—"} → {r.end_time ?? "—"}
                    </td>
                    <td className="p-2 text-ink">{r.artist_name}</td>
                    <td className="p-2 text-ink-2">{r.stage_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
